import React, {
  useState,
  useMemo,
  useEffect,
  useContext,
  useRef
} from "react";
import "./OrdersGrid.css";
import OperationsPanel from "../OperationalPannel/OperationalPannel";
import { getOrdersData } from "../../Data/Kanchi/OMOrderData";
import bgImage from "../../Assets/KOTTracker/banana-leaf-pattern.png";
import OrderCard from "./OrderCard";
import { KanchiOMAuthContext } from "../../../ContextAPI/KanchiKisok";

const OrdersGrid = () => {
  const { token, isAuthenticated } = useContext(KanchiOMAuthContext);

  const [orders, setOrders] = useState([]);
  const [expandedId, setExpandedId] = useState(null);

  const [selectedStatus, setSelectedStatus] = useState("ALL");
  const [selectedTime, setSelectedTime] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");

  const wsRef = useRef(null);
  const reconnectTimeout = useRef(null);
  const reconnectAttempts = useRef(0);
  const MAX_RECONNECTS = 5;

  // ================================
  // 1️⃣ Initial REST Load
  // ================================
  useEffect(() => {
    const loadOrders = async () => {
      try {
        console.log("📡 Fetching initial orders...");
        const data = await getOrdersData();
        setOrders(data);
        console.log("✅ Orders loaded:", data.length);
      } catch (error) {
        console.error("❌ Failed to load orders:", error);
      }
    };

    loadOrders();
  }, []);

  // ================================
  // 2️⃣ WebSocket Connection
  // ================================
  useEffect(() => {
    console.log("🧠 Auth State:", {
      isAuthenticated,
      tokenExists: !!token
    });

    if (!isAuthenticated || !token) {
      console.warn("⚠️ WebSocket not started. Missing auth.");
      return;
    }

    if (wsRef.current) {
      console.warn("⚠️ WebSocket already running.");
      return;
    }

    const connectWebSocket = () => {
      const protocol =
        window.location.protocol === "https:" ? "wss" : "ws";

      const wsUrl = `${protocol}://fliplyn-b-c.fly.dev/ws/orders?token=${token}`;

      console.log("🔌 Connecting to:", wsUrl);

      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          console.log("✅ WebSocket Connected");
          reconnectAttempts.current = 0;
        };

        ws.onmessage = (event) => {
          if (!event?.data) {
            console.warn("⚠️ Empty WebSocket message received.");
            return;
          }

          console.log("📩 Raw WS Message:", event.data);

          let message;

          try {
            message = JSON.parse(event.data);
          } catch (err) {
            console.error("❌ JSON Parse Error:", err);
            console.error("Invalid Payload:", event.data);
            return;
          }

          console.log("📦 Parsed Message:", message);

          if (message.type === "NEW_ORDER") {
            const order = message.order;

            setOrders((prev) => {
              const exists = prev.find((o) => o.id === order.id);

              if (exists) {
                return prev.map((o) =>
                  o.id === order.id
                    ? { ...o, status: order.order_status }
                    : o
                );
              }

              const createdAt = new Date(order.created_at);

              const transformedOrder = {
                id: order.id,
                table: `T-${order.table_number}`,
                name: order.user_name,
                phone: order.user_phone,
                guests: order.number_of_guests,
                time: Math.floor(
                  (Date.now() - createdAt.getTime()) / (1000 * 60)
                ),
                status: order.order_status,
                orderTime: createdAt.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit"
                }),
                items: order.order_items.map((item) => ({
                  name: item.item_name,
                  qty: item.quantity,
                  price: Number(item.total_amount)
                })),
                billing: {
                  net: Number(order.net_amount),
                  cgst: Number(order.cgst),
                  sgst: Number(order.sgst),
                  round: Number(order.round_off),
                  total: Number(order.total_amount_to_pay)
                }
              };

              return [transformedOrder, ...prev];
            });
          }

          if (message.type === "STATUS_UPDATE") {
            setOrders((prev) =>
              prev.map((order) =>
                order.id === message.order_id
                  ? { ...order, status: message.status }
                  : order
              )
            );
          }
        };

        ws.onerror = (error) => {
          console.error("🚨 WebSocket Error Triggered");
          console.error("Error Event:", error);
          console.error("ReadyState:", ws.readyState);
        };

        ws.onclose = (event) => {
          console.warn("❌ WebSocket Closed");
          console.warn("Close Code:", event.code);
          console.warn("Reason:", event.reason || "No reason provided");
          console.warn("Was Clean:", event.wasClean);

          wsRef.current = null;

          if (reconnectAttempts.current < MAX_RECONNECTS) {
            reconnectAttempts.current++;
            console.log(
              `🔁 Reconnect attempt ${reconnectAttempts.current}/${MAX_RECONNECTS} in 3s...`
            );

            reconnectTimeout.current = setTimeout(() => {
              connectWebSocket();
            }, 3000);
          } else {
            console.error("🚨 Max reconnect attempts reached.");
          }
        };
      } catch (err) {
        console.error("❌ WebSocket Initialization Failed:", err);
      }
    };

    connectWebSocket();

    return () => {
      console.log("🧹 Cleaning WebSocket");

      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
      }

      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch (err) {
          console.error("Error while closing WebSocket:", err);
        }
        wsRef.current = null;
      }
    };
  }, [token, isAuthenticated]);

  // ================================
  // Expand / Collapse
  // ================================
  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  // ================================
  // Manual Status Update
  // ================================
  const handleStatusUpdate = async (id, newStatus) => {
    try {
      console.log("🔄 Updating Status:", id, newStatus);

      await fetch(`https://fliplyn-b-c.fly.dev/orders/cafe/${id}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          order_status: newStatus
        })
      });

      setOrders((prev) =>
        prev.map((order) =>
          order.id === id
            ? { ...order, status: newStatus }
            : order
        )
      );
    } catch (error) {
      console.error("❌ Status Update Failed:", error);
    }
  };

  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      const statusMatch =
        selectedStatus === "ALL" ||
        order.status === selectedStatus;

      const timeMatch =
        selectedTime === "ALL" ||
        (selectedTime === "30" && order.time <= 30) ||
        (selectedTime === "60" && order.time <= 60) ||
        (selectedTime === "60+" && order.time > 60);

      const searchMatch =
        order.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.phone.includes(searchTerm);

      return statusMatch && timeMatch && searchMatch;
    });
  }, [orders, selectedStatus, selectedTime, searchTerm]);

  return (
    <div className="kc-main-container">
      <OperationsPanel
        orders={orders}
        selectedStatus={selectedStatus}
        setSelectedStatus={setSelectedStatus}
        selectedTime={selectedTime}
        setSelectedTime={setSelectedTime}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
      />

      <div
        className="kc-orders-wrapper"
        style={{ backgroundImage: `url(${bgImage})` }}
      >
        <div className="kc-overlay" />

        <div className="kc-orders-grid">
          {filteredOrders.map((order) => (
            <OrderCard
              key={order.id}
              order={order}
              expandedId={expandedId}
              toggleExpand={toggleExpand}
              handleStatusUpdate={handleStatusUpdate}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default OrdersGrid;